import { push } from 'react-router-redux';
import { logCheckpointOnPageChange } from '@eb/janus';
import {
    trackEventFromState,
    trackEventFromStateOnPageUnload,
} from '@eb/site-analytics';

import { setWindowLocation } from 'js-utils/http';
import { join as joinPaths } from 'js-utils/path';

import { setAssortment as setAssortmentApi } from '../api';
import {
    ACCOUNT_SETTINGS_UPGRADE_SUCCESS_PATH,
    BASE_URL,
    CONTACT_SALES_URL,
    COPY_PATH,
    PACKAGE_ONE,
    PACKAGE_ONE_CREATE_PATH,
    PACKAGE_SELECTION_PATH,
    PACKAGE_TWO,
    PACKAGE_TWO_ADDON_CREATE_PATH,
    PACKAGE_TWO_CREATE_PATH,
} from '../constants/main';
import { isActiveForAU, trackAnalyticsEventAU } from '../containers/utils';
import {
    GA_CHOOSE_PACKAGE,
    GA_COPY_MIGRATION_MSG_CLICK,
    GA_COPY_MIGRATION_PACKAGE_SELECTION_CLICK,
    GA_COPY_NON_ADMIN_MIGRATION_MSG_CLICK,
    GA_CREATE_MIGRATION_MSG_CLICK,
    GA_CREATE_MIGRATION_PACKAGE_SELECTION_CLICK,
    GA_CREATE_NON_ADMIN_MIGRATION_MSG_CLICK,
    GA_LABEL,
    GA_MIGRATION_CATEGORY,
    GA_P1_LABEL,
    GA_P2_LABEL,
    GA_P3_LABEL,
    GA_PACKAGE_UPGRADED,
    SIGNUP_PACKAGE_SELECTION_1,
    SIGNUP_PACKAGE_SELECTION_2,
    SIGNUP_PACKAGE_SELECTION_2_ADDON,
    SIGNUP_PACKAGE_SELECTION_3,
} from '../constants/analytics';

export const _setPackage = ({
    plan,
    redirectUrl,
    janusCheckpoint,
    domain,
    activeScopeUserId,
}) => () =>
    setAssortmentApi({ plan, activeScopeUserId })
        .then(() => {
            if (janusCheckpoint) {
                logCheckpointOnPageChange(janusCheckpoint, domain);
            }
            if (redirectUrl) {
                setWindowLocation(redirectUrl);
            }
        })
        .catch((error) => {
            throw error;
        });

export const setPackageOne = () => (dispatch, getState) => {
    const {
        user: { isCreate, isCopy, activeScopeUserId },
        ...state
    } = getState();
    const action = _setPackage({
        plan: PACKAGE_ONE,
        redirectUrl: PACKAGE_ONE_CREATE_PATH,
        activeScopeUserId,
    });

    let eventData = null;

    if (isCreate) {
        eventData = {
            action: GA_CREATE_MIGRATION_PACKAGE_SELECTION_CLICK,
            label: GA_P1_LABEL,
            category: GA_MIGRATION_CATEGORY,
        };
    } else if (isCopy) {
        eventData = {
            action: GA_COPY_MIGRATION_PACKAGE_SELECTION_CLICK,
            label: GA_P1_LABEL,
            category: GA_MIGRATION_CATEGORY,
        };
    }

    if (eventData) {
        trackEventFromState(state, eventData);
    }

    dispatch(action);
};

export const setPackageTwo = () => (dispatch, getState) => {
    const {
        user: {
            isCreate,
            canAccessLegacyConvert,
            isFromAccount,
            activeScopeUserId,
            userId,
            userPlan,
        },
        env: { locale },
        featureFlags: { launchPackageEssentialV2 },
        assortments: {
            assortmentsAvailability: {
                current: { version },
            },
        },
        ...state
    } = getState();
    let eventData = null;
    let redirectUrl = PACKAGE_TWO_CREATE_PATH;

    if (canAccessLegacyConvert && isCreate) {
        eventData = {
            action: GA_CREATE_NON_ADMIN_MIGRATION_MSG_CLICK,
            label: GA_P2_LABEL,
            category: GA_MIGRATION_CATEGORY,
        };
    } else if (!canAccessLegacyConvert && isCreate) {
        eventData = {
            action: GA_CREATE_MIGRATION_PACKAGE_SELECTION_CLICK,
            label: GA_P2_LABEL,
            category: GA_MIGRATION_CATEGORY,
        };
    }

    if (isFromAccount) {
        redirectUrl = ACCOUNT_SETTINGS_UPGRADE_SUCCESS_PATH;
    }

    const action = _setPackage({
        plan: PACKAGE_TWO,
        redirectUrl,
        activeScopeUserId,
    });

    if (eventData) {
        trackEventFromState(state, eventData);
    }
    if (isActiveForAU({ locale, launchPackageEssentialV2 })) {
        trackAnalyticsEventAU({
            action: GA_PACKAGE_UPGRADED,
            category: GA_CHOOSE_PACKAGE,
            dimensions: {
                userId,
                userPlan,
                version,
                locale,
            },
        });
    }

    dispatch(action);
};

const _getCopySuccessPath = (copyPath, eid, crumb) =>
    `${copyPath}&eid=${eid}&crumb=${crumb}`;
const _getCoyoteCopySuccessPath = (eid) => `/manage/events/${eid}/copy`;

export const copySetPackageTwo = () => (dispatch, getState) => {
    const {
        env: { eid, crumb },
        user: {
            canAccessLegacyConvert,
            userId,
            isCoyoteCopy,
            activeScopeUserId,
        },
        ...state
    } = getState();
    const copyPath = isCoyoteCopy
        ? _getCoyoteCopySuccessPath(eid)
        : _getCopySuccessPath(COPY_PATH, eid, crumb);
    let eventData = null;

    if (canAccessLegacyConvert) {
        eventData = {
            action: GA_COPY_NON_ADMIN_MIGRATION_MSG_CLICK,
            label: GA_P2_LABEL,
            category: GA_MIGRATION_CATEGORY,
            userId,
        };
    } else {
        eventData = {
            action: GA_COPY_MIGRATION_PACKAGE_SELECTION_CLICK,
            label: GA_P2_LABEL,
            category: GA_MIGRATION_CATEGORY,
            userId,
        };
    }

    const action = _setPackage({
        plan: PACKAGE_TWO,
        redirectUrl: copyPath,
        activeScopeUserId,
    });

    if (eventData) {
        trackEventFromState({ eventId: eid, ...state }, eventData);
    }

    dispatch(action);
};

export const onboardingRedirectToContactSales = () => (dispatch, getState) => {
    const state = getState();
    const {
        env: { eid, crumb },
        user: { isCopy, isCoyoteCopy, isCreate },
    } = state;
    let redirectUrl = CONTACT_SALES_URL;

    if (isCreate) {
        redirectUrl = `${CONTACT_SALES_URL}/?origin=create`;
        trackEventFromStateOnPageUnload(state, {
            action: GA_CREATE_MIGRATION_PACKAGE_SELECTION_CLICK,
            label: GA_P3_LABEL,
            category: GA_MIGRATION_CATEGORY,
        });
    } else if (isCopy) {
        redirectUrl = `${CONTACT_SALES_URL}/?origin=copy&eid=${eid}&crumb=${crumb}`;

        if (isCoyoteCopy) {
            redirectUrl = `${redirectUrl}&isCoyoteCopy=1`;
        }
        trackEventFromStateOnPageUnload(state, {
            action: GA_COPY_MIGRATION_PACKAGE_SELECTION_CLICK,
            label: GA_P3_LABEL,
            category: GA_MIGRATION_CATEGORY,
        });
    }

    setWindowLocation(redirectUrl);
};

export const goToPackageSelection = () => (dispatch, getState) => {
    const {
        user: { isCreate, isCopy },
    } = getState();
    let eventData = null;

    if (isCreate) {
        eventData = {
            action: GA_CREATE_MIGRATION_MSG_CLICK,
            label: GA_LABEL,
            category: GA_MIGRATION_CATEGORY,
        };
    } else if (isCopy) {
        eventData = {
            action: GA_COPY_MIGRATION_MSG_CLICK,
            label: GA_LABEL,
            category: GA_MIGRATION_CATEGORY,
        };
    }

    dispatch({
        ...push(joinPaths(BASE_URL, PACKAGE_SELECTION_PATH)),
        meta: {
            analytics: eventData,
        },
    });
};

export const setSignupPackageOne = () => (dispatch, getState) => {
    const {
        env: { domain },
        user: { activeScopeUserId },
    } = getState();

    dispatch(
        _setPackage({
            plan: PACKAGE_ONE,
            redirectUrl: PACKAGE_ONE_CREATE_PATH,
            janusCheckpoint: SIGNUP_PACKAGE_SELECTION_1,
            domain,
            activeScopeUserId,
        }),
    );
};

export const setSignupPackageTwo = () => (dispatch, getState) => {
    const {
        env: { domain },
        user: { activeScopeUserId },
    } = getState();

    dispatch(
        _setPackage({
            plan: PACKAGE_TWO,
            redirectUrl: PACKAGE_TWO_CREATE_PATH,
            janusCheckpoint: SIGNUP_PACKAGE_SELECTION_2,
            domain,
            activeScopeUserId,
        }),
    );
};

export const setSignupPackageTwoAddon = () => (dispatch, getState) => {
    const {
        env: { domain },
        user: { activeScopeUserId },
    } = getState();

    dispatch(
        _setPackage({
            plan: PACKAGE_TWO,
            redirectUrl: PACKAGE_TWO_ADDON_CREATE_PATH,
            janusCheckpoint: SIGNUP_PACKAGE_SELECTION_2_ADDON,
            domain,
            activeScopeUserId,
        }),
    );
};

export const setSignupPackageThree = () => (dispatch, getState) => {
    const {
        env: { domain },
    } = getState();
    const redirectUrl = `${CONTACT_SALES_URL}/?origin=signup`;

    logCheckpointOnPageChange(SIGNUP_PACKAGE_SELECTION_3, domain);
    setWindowLocation(redirectUrl);
};

export const redirectToReferer = (redirectUrl = '/') => () =>
    setWindowLocation(redirectUrl);
